import { ThemeProvider } from '@mui/material/styles';
import { Suspense, useMemo } from 'react';
import { Route, Routes } from 'react-router-dom';
import { Auth0Protected } from './components/routing/Auth0Protected.jsx';
import AuthedRoute from './components/routing/AuthedRoute';
import NonAuthedRoute from './components/routing/NonAuthedRoute';
import SpinningLoader from './components/SpinningLoader/SpinningLoader';
import { useAuth } from './hooks/useAuth.js';
import SignInSso from './pages/signIn/SignInSso.jsx';
import { noAuthRoutes } from './routes/noAuthRoutes.js';
import { starsMonitorLiteRoutes } from './routes/starsMonitorLiteRoutes.js';
import { starsMonitorPeerAnalysisRoutes } from './routes/starsMonitorPeerAnalysisRoutes.js';
import { starsMonitorRoutes } from './routes/starsMonitorRoutes.js';
import { theme } from './themes/theme';
import PreNavigationRouteBlockModal from './components/PreNavigationRouteBlockModal.jsx';
import { useRecoilState } from 'recoil';
import { allowedRoutesState } from './state/allowedRoutesState';

const App = () => {
  const { user, isLoading, logout } = useAuth();
  const [allowedRoutes, setAllowedRoutes] = useRecoilState(allowedRoutesState);
  if (window.location.host === 'modeler.starsmonitor.com') {
    console.log = () => {};
    console.debug = () => {};
    console.info = () => {};
    console.warn = () => {};
  }

  const authRoutes = useMemo(() => {
    if (!user) {
      //still checks auth guard
      return starsMonitorRoutes;
    }
    const starsMonitor = user?.products?.find((p) => p.name === process.env.REACT_APP_PRODUCT_NAME);

    if (!starsMonitor) {
      //should never happen but assume they don't have access to the app
      logout();
      return null;
    }

    if (starsMonitor?.package === 'plus') {
      setAllowedRoutes(starsMonitorPeerAnalysisRoutes);
      return starsMonitorPeerAnalysisRoutes;
    }

    if (starsMonitor?.package === 'light') {
      setAllowedRoutes(starsMonitorLiteRoutes);
      return starsMonitorLiteRoutes;
    }
    setAllowedRoutes(starsMonitorRoutes);
    return starsMonitorRoutes;
  }, [user]);

  if (isLoading) {
    return <SpinningLoader />;
  }

  //we always need a /directory for sso landing

  return (
    <ThemeProvider theme={theme}>
      <Suspense fallback={<SpinningLoader />}>
        <Routes>
          <Route path={'/signin-sso'} element={<Auth0Protected component={SignInSso} />} />
          {noAuthRoutes.map((route, index) => (
            <Route
              key={index}
              path={route.path}
              element={
                <NonAuthedRoute>
                  <route.component menuType={route.menuType} />
                </NonAuthedRoute>
              }
            />
          ))}

          {authRoutes &&
            authRoutes.map((route, index) => (
              <Route
                key={index}
                path={route.path}
                element={
                  <PreNavigationRouteBlockModal allowedRoutes={[...authRoutes, ...noAuthRoutes]}>
                    <AuthedRoute>
                      <route.component menuType={route.menuType} listName={route.listName} />
                    </AuthedRoute>
                  </PreNavigationRouteBlockModal>
                }
              />
            ))}
        </Routes>
      </Suspense>
    </ThemeProvider>
  );
};

export default App;
